import { useCallback, useEffect, useState, type DragEvent, type MouseEvent } from "react";
import { ChevronDown, ChevronRight, File, Folder, FolderOpen, Link2 } from "lucide-react";
import type { DirEntry } from "@racler/protocol";
import { useRelay } from "../lib/relayContext.tsx";
import { checkMove, joinPath, parentOf } from "../lib/paths.ts";

export { joinPath, parentOf };

export interface TreeSelection {
  path: string;
  kind: DirEntry["kind"];
}

/** Internal drag-and-drop state, owned by the Explorer. External (OS) drags are ignored: there is no source. */
export interface DndProps {
  dragSource: TreeSelection | null;
  /** Directory ("" = root) currently highlighted as the drop target. */
  dropTarget: string | null;
  onDragStart: (s: TreeSelection) => void;
  onDragEnd: () => void;
  onDropTarget: (dir: string | null) => void;
  onDrop: (destDir: string) => void;
  onUploadFiles?: (destDir: string, files: File[]) => void;
}

/** Drag-over / drop handlers for "drop into directory `dest`" (rows, the Project row and the empty tree area). */
export function dropProps(dest: string, dnd: DndProps) {
  return {
    onDragOver: (ev: DragEvent) => {
      if (dnd.dragSource) {
        ev.stopPropagation();
        if (checkMove(dnd.dragSource, dest).ok) {
          ev.preventDefault();
          ev.dataTransfer.dropEffect = "move";
          if (dnd.dropTarget !== dest) dnd.onDropTarget(dest);
        } else {
          ev.dataTransfer.dropEffect = "none";
          if (dnd.dropTarget !== null) dnd.onDropTarget(null);
        }
      } else if (ev.dataTransfer.types.includes("Files")) {
        ev.stopPropagation();
        ev.preventDefault();
        ev.dataTransfer.dropEffect = "copy";
        if (dnd.dropTarget !== dest) dnd.onDropTarget(dest);
      }
    },
    onDrop: (ev: DragEvent) => {
      if (dnd.dragSource) {
        ev.preventDefault();
        ev.stopPropagation();
        dnd.onDrop(dest);
      } else if (ev.dataTransfer.files && ev.dataTransfer.files.length > 0 && dnd.onUploadFiles) {
        ev.preventDefault();
        ev.stopPropagation();
        dnd.onDropTarget(null);
        dnd.onUploadFiles(dest, Array.from(ev.dataTransfer.files));
      }
    },
  };
}

interface Props extends DndProps {
  machineId: string;
  projectId: string;
  activePath: string | null;
  selected: TreeSelection | null;
  /** Directory paths whose listing must be reloaded; bump `version` to trigger. */
  refresh: { version: number; dirs: Set<string> };
  onOpen: (path: string) => void;
  onSelect: (sel: TreeSelection) => void;
  onContextMenu: (sel: TreeSelection, x: number, y: number) => void;
  /** Folders that should be expanded (e.g. after creating an entry inside them). */
  expand: Set<string>;
}

/** Lazy file tree: each directory is listed on first expand (Project-relative paths only). */
export function FileTree(props: Props) {
  const root = dropProps("", props);
  return (
    <div
      className={`tree${props.dropTarget === "" ? " drop-root" : ""}`}
      data-testid="file-tree"
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) props.onDropTarget(null);
      }}
      {...root}
    >
      <Directory {...props} path="" depth={0} />
    </div>
  );
}

function Directory(props: Props & { path: string; depth: number }) {
  const { machineId, projectId, activePath, selected, refresh, onOpen, onSelect, onContextMenu } = props;
  const { expand, path, depth, dragSource, dropTarget, onDragStart, onDragEnd } = props;
  const relay = useRelay();
  const [entries, setEntries] = useState<DirEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const load = useCallback(() => {
    relay
      .machine(machineId, "filesystem/listDirectory", { projectId, path })
      .then((r) => (setEntries(r.entries), setError(null)))
      .catch((e: unknown) => setError((e as Error).message));
  }, [relay, machineId, projectId, path]);
  useEffect(load, [load]);

  // Reload when an operation touched this directory.
  const touched = refresh.dirs.has(path);
  useEffect(() => {
    if (touched) load();
  }, [refresh.version, touched, load]);

  useEffect(() => {
    const toOpen = [...expand]
      .filter((p) => parentOf(p) === path)
      .map((p) => p.slice(path ? path.length + 1 : 0));
    if (toOpen.length) setOpen((o) => ({ ...o, ...Object.fromEntries(toOpen.map((n) => [n, true])) }));
  }, [expand, path]);

  const pad = { paddingLeft: depth * 12 + 8 };
  if (error)
    return (
      <div className="tree-error" style={pad}>
        {error}
      </div>
    );
  if (!entries)
    return (
      <div className="muted small" style={pad}>
        …
      </div>
    );
  if (entries.length === 0 && depth > 0)
    return (
      <div className="muted small" style={pad}>
        empty
      </div>
    );

  return (
    <ul>
      {entries.map((e) => {
        const p = joinPath(path, e.name);
        const isDir = e.kind === "directory";
        const sel: TreeSelection = { path: p, kind: e.kind };
        const isSelected = selected?.path === p;
        const isOpen = !!open[e.name];
        const dest = isDir ? p : path;
        const Icon = isDir ? (isOpen ? FolderOpen : Folder) : e.kind === "symlink" ? Link2 : File;
        const classes = [
          "tree-item",
          activePath === p ? "active" : "",
          isSelected ? "selected" : "",
          isDir && dropTarget === p ? "drop-target" : "",
          dragSource?.path === p ? "dragging" : "",
        ].join(" ");
        return (
          <li key={e.name}>
            <button
              className={classes}
              style={pad}
              draggable
              onDragStart={(ev) => {
                ev.dataTransfer.setData("text/plain", p);
                ev.dataTransfer.effectAllowed = "move";
                onDragStart(sel);
              }}
              onDragEnd={onDragEnd}
              {...dropProps(dest, props)}
              onClick={() => {
                onSelect(sel);
                if (isDir) setOpen((o) => ({ ...o, [e.name]: !o[e.name] }));
                else onOpen(p);
              }}
              onContextMenu={(ev: MouseEvent) => {
                ev.preventDefault();
                onSelect(sel);
                onContextMenu(sel, ev.clientX, ev.clientY);
              }}
              title={p}
              data-path={p}
            >
              <span className="tree-chevron" aria-hidden>
                {isDir && (isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />)}
              </span>
              <Icon size={14} strokeWidth={1.75} className={`tree-icon${isDir ? " dir" : ""}`} aria-hidden />
              <span className="tree-name">{e.name}</span>
            </button>
            {isDir && isOpen && <Directory {...props} path={p} depth={depth + 1} />}
          </li>
        );
      })}
    </ul>
  );
}
